<script setup lang="ts">
/**
 * 填充 / 描边字段：纯色走 `ColorField`，渐变则展开色标编辑器。
 *
 * 纪律：
 *  · 所有改动都产出 **一条可撤销命令**（`core/ops/gradient.ts`）；
 *  · 滑杆/取色器这类连续输入带 `mergeKey`，拖一次只占一条 undo；
 *  · 文档一变（`version`）就重算，绝不缓存 DOM 快照。
 *
 * 边界：`gradientUnits="userSpaceOnUse"` 的渐变（作者手写绝对坐标）**不在这里调方向/中心** ——
 * 那会写出不匹配的 bbox 坐标，反而把作者的东西改坏。此时只给色标编辑，并明示原因。
 */
import { computed } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { lightness, parseColor, toHex, withLightness, type Rgba } from '@/core/color';
import { applyGradient, removeGradient, updateGradient, type GradientSpec } from '@/core/ops/gradient';
import { gradientCss, readGradient, type GradientInfo, type GradientStop, type GradientType } from '@/core/query/gradient';
import ColorField from './ColorField.vue';
import SliderField from './SliderField.vue';

const props = defineProps<{
  value: string | null;
  which?: 'fill' | 'stroke';
  allowNone?: boolean;
}>();
const emit = defineEmits<{ solid: [value: string | null] }>();

const editor = useEditor();
const which = computed(() => props.which ?? 'fill');
const MAX_STOPS = 8;

/** 第一个选中元素上的渐变（不是渐变 → null） */
const info = computed<GradientInfo | null>(() => {
  void editor.version.value; // 文档一变就重算
  const root = editor.svg.value;
  const el = editor.selection.value[0];
  return root && el ? readGradient(root, el, which.value) : null;
});

const css = computed(() => (info.value ? gradientCss(info.value) : ''));
/** userSpaceOnUse：方向/中心的坐标含义不同，不在此调整 */
const lockedGeometry = computed(() => info.value?.units === 'userSpaceOnUse');
const canEdit = computed(() => editor.selection.value.length > 0);

const apply = (spec: Partial<GradientSpec>, mergeKey?: string): void => {
  const root = editor.svg.value;
  const g = info.value;
  if (!root || !g) return;
  const full: GradientSpec = {
    type: spec.type ?? g.type,
    stops: spec.stops ?? g.stops,
    angle: spec.angle ?? g.angle,
    length: spec.length ?? g.length,
    radial: spec.radial ?? g.radial,
  };
  editor.execute(updateGradient(root, g.id, full, { mergeKey }));
};

const setAngle = (deg: number): void => apply({ angle: deg }, `grad-angle:${info.value?.id}`);
const setRadial = (key: 'cx' | 'cy' | 'r', value: number): void => {
  const g = info.value;
  if (g) apply({ radial: { ...g.radial, [key]: value / 100 } }, `grad-${key}:${g.id}`);
};
const setStopColor = (i: number, hex: string): void => {
  const g = info.value;
  if (g) apply({ stops: withStop(g.stops, i, { color: hex }) }, `grad-stop-c:${i}:${g.id}`);
};
const setStopOffset = (i: number, percent: number): void => {
  const g = info.value;
  if (g) apply({ stops: withStop(g.stops, i, { offset: percent / 100 }) }, `grad-stop-o:${i}:${g.id}`);
};

const addStop = (): void => {
  const g = info.value;
  if (!g || g.stops.length >= MAX_STOPS) return;
  const sorted = [...g.stops].sort((a, b) => a.offset - b.offset);
  let gap = -1;
  let at = 0;
  for (let i = 0; i < sorted.length - 1; i++) {
    const d = (sorted[i + 1] as GradientStop).offset - (sorted[i] as GradientStop).offset;
    if (d > gap) {
      gap = d;
      at = i;
    }
  }
  const a = sorted[at] as GradientStop;
  const b = sorted[at + 1] as GradientStop;
  sorted.splice(at + 1, 0, {
    color: mixHex(a.color, b.color, 0.5),
    offset: (a.offset + b.offset) / 2,
    opacity: (a.opacity + b.opacity) / 2,
  });
  apply({ stops: sorted });
};

const removeStop = (i: number): void => {
  const g = info.value;
  if (!g || g.stops.length <= 2) return; // 最少两个色标
  apply({ stops: g.stops.filter((_, k) => k !== i) });
};

/** 反向 = 顺序颠倒 + offset 镜像（不是简单倒序，否则色带位置不对） */
const reverse = (): void => {
  const g = info.value;
  if (!g) return;
  apply({ stops: g.stops.map((s) => ({ ...s, offset: 1 - s.offset })).reverse() });
};

const setType = (type: GradientType): void => apply({ type });

/** 转纯色：取第一个色标色，无人引用时节点一并删除 */
const toSolid = (): void => {
  const root = editor.svg.value;
  const g = info.value;
  if (root && g) editor.execute(removeGradient(root, editor.selection.value, g.id, { which: which.value }));
};

/** 纯色 → 渐变：用当前色 + 它提亮 22% 的版本做两个色标（上下方向） */
const makeGradient = (): void => {
  const root = editor.svg.value;
  const targets = editor.selection.value;
  if (!root || targets.length === 0) return;
  const base: Rgba = parseColor(props.value ?? '') ?? (parseColor('#6366f1') as Rgba);
  const light = withLightness(base, Math.min(0.96, lightness(base) + 0.22));
  editor.execute(
    applyGradient(
      root,
      targets,
      {
        type: 'linear',
        angle: 90,
        stops: [
          { color: toHex(base, { alpha: false }), offset: 0, opacity: 1 },
          { color: toHex(light, { alpha: false }), offset: 1, opacity: 1 },
        ],
      },
      { which: which.value },
    ),
  );
};

/* ------------------------------------------------------------------ 小工具 */
const withStop = (stops: GradientStop[], i: number, patch: Partial<GradientStop>): GradientStop[] =>
  stops.map((s, k) => (k === i ? { ...s, ...patch } : s));

/** 原生取色器只认 #rrggbb */
const hexOf = (color: string): string => {
  const rgba = parseColor(color);
  return rgba ? toHex(rgba, { alpha: false }) : '#000000';
};

const mixHex = (a: string, b: string, t: number): string => {
  const x = parseColor(a);
  const y = parseColor(b);
  if (!x || !y) return a;
  const mix = (p: number, q: number): number => Math.round(p + (q - p) * t);
  return toHex({ r: mix(x.r, y.r), g: mix(x.g, y.g), b: mix(x.b, y.b), a: 1 }, { alpha: false });
};

const pct = (n: number): number => Math.round(n * 100);
</script>

<template>
  <!-- 纯色态 -->
  <div v-if="!info" class="paint-field">
    <ColorField :value="value" :allow-none="allowNone" @change="emit('solid', $event)" />
    <div class="field">
      <span class="grow" />
      <button
        class="ghost-btn"
        data-field="paint-gradient"
        :disabled="!canEdit"
        :title="canEdit ? '转成线性渐变' : '先选中元素'"
        @click="makeGradient"
      >
        渐变
      </button>
    </div>
  </div>

  <!-- 渐变态 -->
  <div v-else class="paint-field">
    <div class="grad-bar" :style="{ background: css }" data-field="grad-preview" />

    <div class="field">
      <label>类型</label>
      <div class="seg">
        <button data-field="grad-type-linear" :class="{ 'is-active': info.type === 'linear' }" @click="setType('linear')">线性</button>
        <button data-field="grad-type-radial" :class="{ 'is-active': info.type === 'radial' }" @click="setType('radial')">径向</button>
      </div>
    </div>

    <template v-if="info.type === 'linear'">
      <div class="field">
        <label>角度</label>
        <SliderField name="grad-angle" :value="Math.round(info.angle)" :min="0" :max="360" @change="setAngle" />
        <span class="panel-note">{{ Math.round(info.angle) }}°</span>
      </div>
    </template>
    <template v-else>
      <div class="field">
        <label>中心 X</label>
        <SliderField name="grad-cx" :value="pct(info.radial.cx)" :min="0" :max="100" @change="setRadial('cx', $event)" />
        <span class="panel-note">{{ pct(info.radial.cx) }}%</span>
      </div>
      <div class="field">
        <label>中心 Y</label>
        <SliderField name="grad-cy" :value="pct(info.radial.cy)" :min="0" :max="100" @change="setRadial('cy', $event)" />
        <span class="panel-note">{{ pct(info.radial.cy) }}%</span>
      </div>
      <div class="field">
        <label>半径</label>
        <SliderField name="grad-r" :value="pct(info.radial.r)" :min="1" :max="150" @change="setRadial('r', $event)" />
        <span class="panel-note">{{ pct(info.radial.r) }}%</span>
      </div>
    </template>

    <p v-if="lockedGeometry" class="panel-note">
      该渐变用的是 userSpaceOnUse 绝对坐标，方向/中心不在面板里改（改了会与作者坐标不一致），色标仍可编辑。
    </p>

    <div class="field">
      <span class="panel-note">色标 {{ info.stops.length }}/{{ MAX_STOPS }}</span>
      <span class="grow" />
      <button class="ghost-btn" data-field="grad-reverse" title="反向（偏移镜像）" @click="reverse">反向</button>
      <button
        class="ghost-btn"
        data-field="grad-add-stop"
        :disabled="info.stops.length >= MAX_STOPS"
        title="在最宽的一段中间插一个色标"
        @click="addStop"
      >
        ＋ 色标
      </button>
    </div>

    <div class="grad-stops">
      <!-- key 只跟下标：色值进 key 会让"改颜色 = 重建这一行"，<input type="color"> 被换掉后
           浏览器原生取色器会当场关闭（用户实测：点色相条就闪没）。 -->
      <div v-for="(s, i) in info.stops" :key="i" class="grad-stop">
        <input
          class="pal-colorinput"
          type="color"
          data-field="grad-stop-color"
          :data-index="i"
          :value="hexOf(s.color)"
          aria-label="色标颜色"
          @input="setStopColor(i, ($event.target as HTMLInputElement).value)"
        />
        <input
          class="grad-offset"
          type="range"
          min="0"
          max="100"
          step="1"
          data-field="grad-stop-offset"
          :data-index="i"
          :value="pct(s.offset)"
          @input="setStopOffset(i, Number(($event.target as HTMLInputElement).value))"
        />
        <span class="panel-note">{{ pct(s.offset) }}%</span>
        <button
          class="ghost-btn"
          data-field="grad-del-stop"
          :disabled="info.stops.length <= 2"
          title="删除色标（至少保留两个）"
          @click="removeStop(i)"
        >
          ✕
        </button>
      </div>
    </div>

    <div class="field">
      <button class="ghost-btn" data-field="grad-to-solid" title="取消渐变，回到纯色" @click="toSolid">转纯色</button>
    </div>
  </div>
</template>
